import { t } from '@lingui/core/macro'
import { getFirstFromSingleElementArray } from '@tecsinapse/es-utils/build'
import difference from 'lodash.difference'
import { USUARIO_BY_CPF_QUERY } from '../data/usuario-by-cpf-query'
import { USUARIO_BY_EMAIL_QUERY } from '../data/usuario-by-email-query'
import yup from '@/core/utils/yup-validation'
import { FieldValues, SubmitHandler } from 'react-hook-form'
import { ApolloError } from '@apollo/client'
import { OptionSelect } from '@/core/utils/types'
import { getLabel } from '@/core/utils/global-labels'
import { TipoDocumento } from '../../../core/enums/tipo-documento'

export const createSchemaArray = (
  produtos,
  client,
  tipoAcessoMap,
  tiposUsuarioDocumentoObrigatorio,
  tiposUsuarioInformarCargo,
  tiposUsuarioInformarGestao
) => {
  const schemaArray = [
    yup.object({
      nome: yup
        .string()
        .required()
        .label(t`Nome`),
      email: yup
        .string()
        .email()
        .required()
        .label(t`E-mail`)
        .test(
          'email-validation',
          t`O e-mail já está sendo utilizado`,
          async function emailAlreadyExistsValidation(value) {
            if (!value || this.parent.id) {
              return true
            }

            const { data } = await client.query({
              query: USUARIO_BY_EMAIL_QUERY,
              variables: { email: value },
              fetchPolicy: 'no-cache'
            })
            return !data.usuarioByEmail
          }
        ),
      cpf: yup
        .string()
        .nullable()
        .cpf(t`O CPF é inválido`)
        .test(
          'cpf-required',
          t`O CPF deve ser informado`,
          async function cpfRequiredValidation(value) {
            if (!this.parent.tipo || this.parent.isEstrangeiro) {
              return true
            }

            return tiposUsuarioDocumentoObrigatorio.some(
              tipo => tipo.id === this.parent.tipo.value
            )
              ? !!value
              : true
          }
        )
        .test(
          'cpf-validation',
          t`O CPF já está sendo utilizado`,
          async function cpfAlreadyExistsValidation(value) {
            if (!value) {
              return true
            }
            const { data } = await client.query({
              query: USUARIO_BY_CPF_QUERY,
              variables: { cpf: value },
              fetchPolicy: 'no-cache'
            })

            return !data.usuarioByCpf || data.usuarioByCpf.id === this.parent.id
          }
        ),
      codigos: yup.array().of(yup.string().required()),
      tipo: yup
        .object()
        .test(
          'has-required-fields',
          t`Tipo Usuário é um campo obrigatório`,
          value => value && !!value.label && !!value.value
        ),
      idsDealers: yup
        .array()
        .of(yup.object().shape({ label: yup.string(), value: yup.string() }))
        .label(getLabel('label.dealers'))
        .test(
          'DealersValidation',
          t`Informe ao menos um item`,
          function (value) {
            const tipo = this.parent?.tipo
            if (tipo?.value === 'DEALER_GRUPO') {
              return (
                Array.isArray(value) &&
                value?.length > 0 &&
                value?.every(dealer => dealer.value)
              )
            }
            return true
          }
        ),
      dataAdmissao: yup
        .date()
        .test(
          'data-admissao-test',
          t`Data de admissão é um campo obrigatório`,
          function (value) {
            const hasCargos =
              this.parent.cargos.length === this.parent.idsDealers.length
            if (
              tiposUsuarioInformarGestao.some(
                tu => tu.id === this.parent.tipo.value
              ) &&
              hasCargos
            ) {
              return value
            }
            return true
          }
        ),
      emailSuperior: yup
        .string()
        .email(t`Endereço de email inválido`)
        .test(
          'email-superior-test',
          t`Email do superior é um campo obrigatório`,
          function (value) {
            const hasCargos =
              this.parent.cargos.length === this.parent.idsDealers.length
            if (
              tiposUsuarioInformarGestao.some(
                tu => tu.id === this.parent.tipo.value
              ) &&
              hasCargos
            ) {
              return value && value.length > 0
            }
            return true
          }
        ),
      dataDesligamento: yup
        .date()
        .nullable()
        .when('dataAdmissao', (dataAdmissao, schema) => {
          return dataAdmissao[0]
            ? schema.min(
                dataAdmissao,
                t`A data de desligamento não pode ser anterior à data de admissão`
              )
            : schema
        })
        .test(
          'required-if-motivo',
          t`É necessário informar a data de desligamento`,
          function (value) {
            const { desligamento } = this.parent
            return !(desligamento?.value && !value)
          }
        ),
      desligamento: yup
        .object({
          value: yup.string(),
          label: yup.string()
        })
        .nullable()
        .test(
          'desligamento-motivo-test',
          t`É necessário informar um motivo`,
          function (field) {
            return !(this.parent?.dataDesligamento && !field?.value)
          }
        ),
      cargos: yup
        .array()
        .of(
          yup.object({
            value: yup.string(),
            label: yup.string(),
            dataInicio: yup.date(),
            dealerId: yup.string(),
            dealerNome: yup.string(),
            deparamentoId: yup.string(),
            deparamentoNome: yup.string()
          })
        )
        .test(
          'cargos-test',
          t`Preencha os cargos do usuário`,
          function (cargoValues) {
            const { idsDealers, tipo } = this.parent
            if (tiposUsuarioInformarCargo.some(tu => tu.id === tipo.value)) {
              if (!Array.isArray(cargoValues) || cargoValues.length === 0) {
                return false
              }

              const missingCargoForSomeDealer = idsDealers.some(dealer => {
                return !cargoValues.some(
                  cargo => cargo.dealerId === dealer.value
                )
              })

              if (missingCargoForSomeDealer) {
                return false
              }
            }
            return true
          }
        )
    }),
    yup.object({
      produtos: yup.object(
        produtos?.reduce(
          (acc, produto) => ({
            ...acc,
            [produto.id]: yup.object({
              funcionalidades: yup.array().of(yup.string()),
              idsTiposAcesso: yup
                .array()
                .of(yup.object())
                .test(
                  'verifica se pelo menos 1 das funcionalidades estão habilitadas para os tipos de acesso',
                  null,
                  function (value: OptionSelect[]) {
                    const idsTiposAcesso = (value ?? []).map(v => v?.value)
                    const { funcionalidades } = this.parent
                    const errorPath = []

                    idsTiposAcesso.forEach(idTipoAcesso => {
                      const tipoAcesso = tipoAcessoMap[idTipoAcesso]
                      const funcionalidadesPadraoId =
                        tipoAcesso?.funcionalidadesPadrao?.map(
                          ({ id }) => id
                        ) ?? []

                      const differenceBettwenSelected = difference(
                        funcionalidadesPadraoId,
                        funcionalidades
                      )
                      if (
                        differenceBettwenSelected?.length ===
                          funcionalidadesPadraoId?.length &&
                        tipoAcesso?.id
                      ) {
                        errorPath.push(tipoAcesso?.nome)
                      }
                    })

                    if (errorPath?.length) {
                      const message =
                        t`Produto ${produto.nome}: Habilite ao menos uma permissão para os acessos:`
                          .concat('\n')
                          .concat(errorPath.join('\n'))
                      return this.createError({
                        message
                      })
                    }
                    return true
                  }
                )
            })
          }),
          {}
        )
      )
    })
  ]

  return schemaArray
}

export const createInitialValues = (
  usuarioQuery,
  produtos,
  marcas,
  tiposUsuario,
  cargos,
  infoUsuario
) => {
  const {
    usuario = {
      id: null,
      nome: '',
      email: '',
      cpf: '',
      tipoDocumento: '',
      tipo: {},
      marcas: [],
      telefones: [],
      celulares: [],
      funcionalidades: [],
      dealers: [],
      grupos: [],
      tiposAcesso: []
    }
  } = usuarioQuery || {}
  const tipoUsuarioUnico = getFirstFromSingleElementArray(tiposUsuario ?? [])
  const { telefones, celulares } = usuario
  const tipo = tipoUsuarioUnico
    ? { value: tipoUsuarioUnico.id, label: tipoUsuarioUnico.description }
    : { value: usuario.tipo.id, label: usuario.tipo.description }
  const dealers = usuario.dealers
    .filter(({ active }) => active)
    .map(dealer => ({
      value: dealer.id,
      label: dealer.nomeExibicao
    }))
  const initialValues = {
    id: usuario.id,
    nome: usuario.nome,
    email: usuario.email,
    cpf: usuario.cpf,
    tipo,
    marcas:
      usuario.marcas.length > 0
        ? usuario.marcas?.map(marca => ({
            value: marca.id,
            label: marca.description
          }))
        : marcas?.map(marca => ({
            value: marca.id,
            label: marca.description
          })),
    telefones: telefones.length > 0 ? telefones : [],
    celulares: celulares.length > 0 ? celulares : [],
    idsGrupos: usuario.grupos.map(grupo => ({
      value: grupo.id,
      label: grupo.nome
    })),
    idsDealers: dealers,
    produtos: usuario.funcionalidades.reduce((acc, funcionalidade) => {
      const idProduto = funcionalidade.produto.id
      if (acc[idProduto]) {
        acc[idProduto].funcionalidades.push(funcionalidade.id)
      } else {
        acc[idProduto] = {
          funcionalidades: [funcionalidade.id],
          idsTiposAcesso: []
        }
      }
      return acc
    }, {}),
    cargos: cargos?.map(cargo => ({
      value: cargo.id,
      label: cargo.nome,
      dataInicio: cargo.dataInicio,
      dealerId: cargo?.dealer?.id ?? '',
      dealerNome: cargo?.dealer?.nome ?? '',
      departamentoId: cargo?.departamento?.id ?? '',
      departamentoNome: cargo?.departamento?.nome ?? ''
    })),
    dataAdmissao: infoUsuario?.dataAdmissao,
    emailSuperior: infoUsuario?.emailSuperior ?? '',
    dataDesligamento: infoUsuario?.dataDesligamento,
    desligamento: infoUsuario?.desligamento
      ? {
          value: infoUsuario.desligamento.id,
          label: infoUsuario.desligamento.motivo || ''
        }
      : null,
    isEstrangeiro: usuario.isEstrangeiro
  }
  usuario.tiposAcesso.reduce((acc, tipoAcesso) => {
    const idProduto = tipoAcesso.produto.id
    if (acc[idProduto]) {
      acc[idProduto].idsTiposAcesso.push({
        label: tipoAcesso.nome,
        value: tipoAcesso.id
      })
    } else {
      acc[idProduto] = {
        funcionalidades: [],
        idsTiposAcesso: [
          {
            label: tipoAcesso.nome,
            value: tipoAcesso.id
          }
        ]
      }
    }
    return acc
  }, initialValues.produtos)
  produtos?.forEach(({ id }) => {
    if (!(id in initialValues.produtos)) {
      initialValues.produtos[id] = {
        funcionalidades: [],
        idsTiposAcesso: []
      }
    }
  })
  return initialValues
}

export const createSubmitHandler = (
  salvarUsuario,
  tiposUsuarioInformarGestao,
  onClose
): SubmitHandler<FieldValues> => {
  return async _usuario => {
    try {
      const {
        idsGrupos,
        cargos = [],
        dataAdmissao,
        emailSuperior,
        dataDesligamento,
        desligamento,
        isEstrangeiro,
        ...usuario
      } = _usuario

      const infos = tiposUsuarioInformarGestao.some(
        tu => tu.id === usuario.tipo.value
      )
        ? {
            dataAdmissao,
            emailSuperior,
            dataDesligamento,
            desligamentoId: desligamento ? desligamento.value : undefined
          }
        : null

      const usuarioInput = {
        ...usuario,
        idsDealers: usuario.idsDealers.map(dealer => dealer.value),
        marcas: usuario.marcas.map(marca => marca.value),
        tipo: usuario.tipo.value,
        cpf: usuario.cpf?.trim() ? usuario.cpf?.trim() : null,
        produtos: Object.keys(usuario.produtos).map(key => {
          const produto = usuario.produtos[key]
          return {
            idProduto: key,
            idsFuncionalidades: produto.funcionalidades ?? [],
            idsTiposAcesso: (produto.idsTiposAcesso ?? []).map(
              tipoAcesso => tipoAcesso.value
            )
          }
        }),
        tipoDocumento: isEstrangeiro
          ? TipoDocumento.ESTRANGEIRO
          : TipoDocumento.CPF
      }

      const { nome } = usuario
      const acao = usuario.id ? t`editado` : t`criado`
      const successMessage = t`Usuário ${nome} ${acao} com sucesso!`

      const cargosInput = cargos.map(it => {
        const dealerId =
          it.dealerId?.length > 0 ? { dealerId: it.dealerId } : {}
        const departamentoId =
          it.departamentoId?.length > 0
            ? { departamentoId: it.departamentoId }
            : {}
        return {
          dataInicio: it.dataInicio,
          cargoId: it.value,
          ...departamentoId,
          ...dealerId
        }
      })

      await salvarUsuario(
        usuarioInput,
        cargosInput,
        infos,
        successMessage,
        onClose
      )
    } catch (error) {
      throw new Error((error as ApolloError).message)
    }
  }
}
